import React, { createContext, useCallback, useContext, useEffect } from 'react'
import { useFetchSingle } from '../hooks/useFetchSingle'
import { PluginData } from '../types/PluginData'
import i18n from '../i18n'
import { ApiRoutes } from '../services/ApiService'
import { Setting, SettingType, ValueTypeMap } from '../types/GlobalSetting'
import { useFetchData } from '../hooks/useFetchData'

type PluginContextType = {
    plugin: PluginData | null
    isGlobalSettingLoading: boolean
    globalSetting: Setting[] | null
    getSettingValue:  <T extends SettingType>(name: string, type: T) => ValueTypeMap[T] | undefined
    fetchGlobalSetting: (params?: Record<string, string>) => void
}

const PluginContext = createContext<PluginContextType | undefined>(undefined)

type PluginContextProps = {
    children: React.ReactNode | React.ReactNode[]
}

export const PluginContextProvider = ({
    children,
}: PluginContextProps): JSX.Element => {
    const { data: plugin, fetch: fetchPlugin } = useFetchSingle<PluginData>(
        ApiRoutes.PLUGIN
    )

    const {
        data: globalSetting,
        isLoading: isGlobalSettingLoading,
        fetch: fetchGlobalSetting,
    } = useFetchData<Setting>(ApiRoutes.GLOBAL_SETTINGS)

    const getSettingValue = useCallback(
    <T extends SettingType>(name: string, type: T): ValueTypeMap[T] | undefined => {
        const setting = globalSetting.find(
            (s) => s.input_type === type && s.name === name
        )
        return setting?.value as ValueTypeMap[T] | undefined
    }, [globalSetting])

    useEffect(() => {
        fetchGlobalSetting()
    }, [fetchGlobalSetting])

    useEffect(() => {
        fetchPlugin(
            window.location.href.includes('admin') ? 'admin' : 'frontend'
        )
    }, [fetchPlugin])

    useEffect(() => {
        if (!plugin) return
        i18n.changeLanguage(plugin.language.i18n).then(() => {
            localStorage.setItem('zr_customizer_language', plugin.language.i18n)
        })
    }, [plugin])

    return (
        <PluginContext.Provider
            value={{
                plugin,
                globalSetting,
                isGlobalSettingLoading,
                getSettingValue,
                fetchGlobalSetting
            }}
        >
            {children}
        </PluginContext.Provider>
    )
}

export const usePluginContext = () => {
    const data = useContext(PluginContext)

    if (data === undefined) {
        throw new Error(
            'usePluginContext must used within PluginContextProvider'
        )
    }

    return data
}
